// src/pages/AreasCovered.js - Redesigned with interactive features
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './AreasCovered.css';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faCheckCircle, 
  faPhoneAlt, 
  faBuilding,
  faHardHat,
  faClock,
  faShieldAlt,
  faInfo,
  faArrowRight,
  faSearch
} from '@fortawesome/free-solid-svg-icons';

function AreasCovered() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArea, setSelectedArea] = useState(null);
  const [animated, setAnimated] = useState(false);
  const [visibleLocations, setVisibleLocations] = useState(8);

  // Primary service areas
  const primaryAreas = [
    { name: 'Glossop', distance: '0', responseTime: 'Under 1 hour', highlight: true },
    { name: 'Hadfield', distance: '2.5', responseTime: '1 hour', highlight: true },
    { name: 'Gamesley', distance: '3', responseTime: '1 hour', highlight: true },
    { name: 'Charlesworth', distance: '4', responseTime: '1 hour', highlight: true },
    { name: 'Simmondley', distance: '2', responseTime: '1 hour', highlight: true },
    { name: 'Tintwistle', distance: '4.5', responseTime: '1-2 hours', highlight: true },
    { name: 'Padfield', distance: '3', responseTime: '1 hour', highlight: true },
    { name: 'Dinting', distance: '1.5', responseTime: 'Under 1 hour', highlight: true }
  ];
  
  // Secondary service areas
  const secondaryAreas = [
    { name: 'Manchester', distance: '15', responseTime: '1-2 hours' },
    { name: 'Stalybridge', distance: '8', responseTime: '1-2 hours' },
    { name: 'Stockport', distance: '12', responseTime: '1-2 hours' },
    { name: 'Hyde', distance: '7', responseTime: '1-2 hours' },
    { name: 'Mottram', distance: '5', responseTime: '1-2 hours' },
    { name: 'Hollingworth', distance: '6', responseTime: '1-2 hours' },
    { name: 'Broadbottom', distance: '6.5', responseTime: '1-2 hours' },
    { name: 'Ashton-under-Lyne', distance: '9', responseTime: '1-2 hours' },
    { name: 'Droylsden', distance: '11', responseTime: '1-2 hours' },
    { name: 'Dukinfield', distance: '8.5', responseTime: '1-2 hours' },
    { name: 'Denton', distance: '10', responseTime: '1-2 hours' },
    { name: 'Marple', distance: '11', responseTime: '1-2 hours' },
    { name: 'New Mills', distance: '7', responseTime: '1-2 hours' },
    { name: 'Chapel-en-le-Frith', distance: '9', responseTime: '1-2 hours' },
    { name: 'Whaley Bridge', distance: '11', responseTime: '1-2 hours' },
    { name: 'Buxton', distance: '15', responseTime: '2-3 hours' }
  ];
  
  // Combine all areas for searching
  const allAreas = [...primaryAreas, ...secondaryAreas];
  
  // Filter areas based on search term
  const filteredAreas = allAreas.filter(area => 
    area.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Animation on scroll
  useEffect(() => {
    const handleScroll = () => {
      const element = document.querySelector('.areas-content');
      if (!element) return;
      
      const position = element.getBoundingClientRect();
      if (position.top < window.innerHeight * 0.75 && !animated) {
        setAnimated(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on initial load
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [animated]);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Reset selected area when searching
    setSelectedArea(null);
  };
  
  // Handle area selection
  const handleAreaSelect = (area) => {
    setSelectedArea(area);
    
    // Update map to focus on selected area
    const mapElement = document.getElementById('areas-map');
    if (mapElement) {
      // Replace the map embed with one focused on the selected area
      // In a real implementation, you would use Google Maps API to update the map focus
      // This is a simplified example
      mapElement.src = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d19046.153818672876!2d-1.9701268288696283!3d53.44262627679333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487bba2e732c35db%3A0x44c68783568fece0!2s${area.name}!5e0!3m2!1sen!2suk!4v1690540545735!5m2!1sen!2suk`;
    }
  };
  
  // Load more locations
  const handleLoadMore = () => {
    setVisibleLocations(prev => prev + 8);
  };
  
  return (
    <div className="areas-covered-page">
      {/* Page Header */}
      <div className="areas-header">
        <div className="container">
          <div className={`page-title-wrapper ${animated ? 'animated' : ''}`}>
            <h1 className="page-title">Areas We Cover</h1>
            <div className="title-separator"></div>
            <p className="page-subtitle">
              Professional roofing services throughout Glossop and the surrounding areas
            </p>
          </div>
        </div>
      </div>
      
      <div className="areas-content">
        <div className="container">
          <div className="areas-intro">
            <div className={`intro-text ${animated ? 'animated' : ''}`}>
              <div className="intro-icon">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </div>
              <h2>Serving Glossop & Beyond</h2>
              <p>
                Based in Old Glossop, we provide reliable roofing services to homes and businesses throughout 
                Glossop and the surrounding areas. From emergency repairs to complete roof installations, 
                our team is ready to deliver quality workmanship wherever you're located.
              </p>
              <p>
                We primarily serve Glossop and its immediate surroundings, but also extend our services to 
                Manchester, Stalybridge, Stockport, and many other locations within the region.
              </p>
            </div>
            
            <div className={`service-highlights ${animated ? 'animated' : ''}`}>
              <div className="service-highlight-card">
                <div className="highlight-icon">
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <h3>Fast Response Time</h3>
                <p>We aim to respond to all enquiries within 24 hours and can offer same-day service for emergencies in our primary service areas.</p>
              </div>
              
              <div className="service-highlight-card">
                <div className="highlight-icon">
                  <FontAwesomeIcon icon={faHardHat} />
                </div>
                <h3>Local Knowledge</h3>
                <p>Our team has extensive knowledge of local building styles and environmental conditions, ensuring appropriate solutions.</p>
              </div>
              
              <div className="service-highlight-card">
                <div className="highlight-icon">
                  <FontAwesomeIcon icon={faShieldAlt} />
                </div>
                <h3>Consistent Quality</h3>
                <p>No matter where you're located, we maintain the same high standards of workmanship and customer service.</p>
              </div>
            </div>
          </div>
          
          {/* Map Section */}
          <div className={`map-section ${animated ? 'animated' : ''}`}>
            <div className="map-section-header">
              <h2>Our Service Area</h2>
              <p>View an interactive map of our coverage area and find your location</p>
            </div>
            
            <div className="map-content">
              <div className="map-container">
                <iframe
                  id="areas-map"
                  title="Areas Covered Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d19046.153818672876!2d-1.9701268288696283!3d53.44262627679333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487bba2e732c35db%3A0x44c68783568fece0!2sGlossop!5e0!3m2!1sen!2suk!4v1690540545735!5m2!1sen!2suk"
                  width="100%"
                  height="500"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
              
              <div className="map-sidebar">
                <div className="area-search">
                  <div className="search-icon">
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                  <input
                    type="text"
                    placeholder="Search your area..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>
                
                {selectedArea ? (
                  <div className="area-details">
                    <div className="area-details-header">
                      <h3>
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="area-icon" />
                        {selectedArea.name}
                      </h3>
                      <button 
                        className="back-btn"
                        onClick={() => setSelectedArea(null)}
                      >
                        View All
                      </button>
                    </div>
                    
                    <div className="area-info-grid">
                      <div className="area-info-item">
                        <div className="info-label">Distance from Glossop:</div>
                        <div className="info-value">{selectedArea.distance} miles</div>
                      </div>
                      
                      <div className="area-info-item">
                        <div className="info-label">Typical Response Time:</div>
                        <div className="info-value">{selectedArea.responseTime}</div>
                      </div>
                      
                      <div className="area-info-item">
                        <div className="info-label">Service Type:</div>
                        <div className="info-value">{selectedArea.highlight ? 'Primary Service Area' : 'Extended Service Area'}</div>
                      </div>
                    </div>
                    
                    <div className="area-services">
                      <h4>Services Available:</h4>
                      <ul className="services-list">
                        <li><FontAwesomeIcon icon={faCheckCircle} /> New Roof Installation</li>
                        <li><FontAwesomeIcon icon={faCheckCircle} /> Roof Repairs</li>
                        <li><FontAwesomeIcon icon={faCheckCircle} /> Roof Maintenance</li>
                        <li><FontAwesomeIcon icon={faCheckCircle} /> Guttering & Fascias</li>
                        <li><FontAwesomeIcon icon={faCheckCircle} /> Chimney Repairs</li>
                        {selectedArea.highlight && (
                          <li><FontAwesomeIcon icon={faCheckCircle} /> 24/7 Emergency Service</li>
                        )}
                      </ul>
                    </div>
                    
                    <div className="area-cta">
                      <Link to="/contact" className="area-quote-btn">
                        Request Quote in {selectedArea.name} <FontAwesomeIcon icon={faArrowRight} />
                      </Link>
                      <a href="tel:07735005695" className="area-call-btn">
                        <FontAwesomeIcon icon={faPhoneAlt} /> Call Now
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="areas-list-container">
                    {searchTerm && filteredAreas.length === 0 ? (
                      <div className="no-areas-found">
                        <FontAwesomeIcon icon={faInfo} className="no-areas-icon" />
                        <p>No areas found matching "{searchTerm}"</p>
                        <p>Please try a different search or contact us to check if we serve your area.</p>
                        <button 
                          className="clear-search"
                          onClick={() => setSearchTerm('')}
                        >
                          Clear Search
                        </button>
                      </div>
                    ) : (
                      <>
                        {searchTerm === '' && (
                          <div className="areas-section">
                            <h3 className="areas-section-title">Primary Service Areas</h3>
                            <div className="areas-grid">
                              {primaryAreas.map((area, index) => (
                                <div 
                                  key={index} 
                                  className="area-card primary"
                                  onClick={() => handleAreaSelect(area)}
                                >
                                  <div className="area-marker">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                                  </div>
                                  <div className="area-name">{area.name}</div>
                                  <span className="area-distance">{area.distance} miles</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {searchTerm === '' && (
                          <div className="areas-section">
                            <h3 className="areas-section-title">Extended Service Areas</h3>
                            <div className="areas-grid">
                              {secondaryAreas.slice(0, visibleLocations).map((area, index) => (
                                <div 
                                  key={index} 
                                  className="area-card secondary"
                                  onClick={() => handleAreaSelect(area)}
                                >
                                  <div className="area-marker">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                                  </div>
                                  <div className="area-name">{area.name}</div>
                                  <span className="area-distance">{area.distance} miles</span>
                                </div>
                              ))}
                            </div>
                            
                            {visibleLocations < secondaryAreas.length && (
                              <div className="areas-load-more">
                                <button 
                                  className="load-more-btn"
                                  onClick={handleLoadMore}
                                >
                                  Load More Areas
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {searchTerm !== '' && (
                          <div className="areas-section">
                            <h3 className="areas-section-title">Search Results</h3>
                            <div className="areas-grid">
                              {filteredAreas.map((area, index) => (
                                <div 
                                  key={index} 
                                  className={`area-card ${area.highlight ? 'primary' : 'secondary'}`}
                                  onClick={() => handleAreaSelect(area)}
                                >
                                  <div className="area-marker">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                                  </div>
                                  <div className="area-name">{area.name}</div>
                                  <span className="area-distance">{area.distance} miles</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Not Sure Section */}
          <div className={`not-sure-section ${animated ? 'animated' : ''}`}>
            <div className="not-sure-content">
              <div className="not-sure-icon">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              <h2>Not Sure If We Cover Your Area?</h2>
              <p>
                If you can't find your location listed above, please get in touch with us directly. 
                We often travel outside our usual service areas for larger projects and may be able to accommodate your needs.
              </p>
              <div className="not-sure-buttons">
                <Link to="/contact" className="primary-button">
                  Contact Us <FontAwesomeIcon icon={faArrowRight} />
                </Link>
                <a href="tel:07735005695" className="secondary-button">
                  <FontAwesomeIcon icon={faPhoneAlt} /> Call: 07735 005 695
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AreasCovered;